import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import WrapperComponent from '../commonComponents/WrapperComponent';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { url } from '../../util/config';

// ── Pre-built connector templates ─────────────────────────────────────────────
const CONNECTOR_TEMPLATES = {
    google_calendar: {
        name:        'Google Calendar',
        icon:        '📅',
        description: 'Book, check, and cancel appointments in Google Calendar',
        method:      'POST',
        url:         'https://www.googleapis.com/calendar/v3/calendars/primary/events',
        headers:     [{ key: 'Authorization', value: 'Bearer {{access_token}}' }, { key: 'Content-Type', value: 'application/json' }],
        params:      [
            { name: 'access_token', description: 'Google OAuth access token', required: true, type: 'string' },
            { name: 'summary',      description: 'Event title',               required: true, type: 'string' },
            { name: 'start_time',   description: 'Start time (ISO format)',   required: true, type: 'string' },
            { name: 'end_time',     description: 'End time (ISO format)',     required: true, type: 'string' },
        ],
        body_template: '{"summary": "{{summary}}", "start": {"dateTime": "{{start_time}}"}, "end": {"dateTime": "{{end_time}}"}}',
        response_mapping: [
            { field: 'id',              save_as: 'event_id',     label: 'Event ID' },
            { field: 'htmlLink',        save_as: 'event_link',   label: 'Event Link' },
            { field: 'status',          save_as: 'event_status', label: 'Event Status' },
        ],
    },
    razorpay: {
        name:        'Razorpay Payment Link',
        icon:        '💳',
        description: 'Generate Razorpay payment links for customers',
        method:      'POST',
        url:         'https://api.razorpay.com/v1/payment_links',
        headers:     [{ key: 'Content-Type', value: 'application/json' }],
        params:      [
            { name: 'amount',      description: 'Amount in paise (e.g. 50000 = ₹500)', required: true,  type: 'number' },
            { name: 'description', description: 'Payment description',                  required: true,  type: 'string' },
            { name: 'customer_name',  description: 'Customer name',                     required: false, type: 'string' },
            { name: 'customer_email', description: 'Customer email',                    required: false, type: 'string' },
            { name: 'customer_phone', description: 'Customer phone',                    required: false, type: 'string' },
        ],
        body_template: '{"amount": {{amount}}, "currency": "INR", "description": "{{description}}", "customer": {"name": "{{customer_name}}", "email": "{{customer_email}}", "contact": "{{customer_phone}}"}, "notify": {"sms": true, "email": true}}',
        response_mapping: [
            { field: 'short_url', save_as: 'payment_link', label: 'Payment Link' },
            { field: 'id',        save_as: 'payment_id',   label: 'Payment ID' },
            { field: 'status',    save_as: 'link_status',  label: 'Link Status' },
        ],
    },
    shopify: {
        name:        'Shopify Order Status',
        icon:        '🛍️',
        description: 'Check order status and details from Shopify',
        method:      'GET',
        url:         'https://{{store_name}}.myshopify.com/admin/api/2024-01/orders/{{order_id}}.json',
        headers:     [{ key: 'X-Shopify-Access-Token', value: '{{access_token}}' }],
        params:      [
            { name: 'store_name',   description: 'Shopify store name (without .myshopify.com)', required: true, type: 'string' },
            { name: 'access_token', description: 'Shopify admin access token',                  required: true, type: 'string' },
            { name: 'order_id',     description: 'Order ID to check',                           required: true, type: 'string' },
        ],
        body_template: '',
        response_mapping: [
            { field: 'order.financial_status', save_as: 'payment_status',   label: 'Payment Status' },
            { field: 'order.fulfillment_status', save_as: 'order_status',   label: 'Order Status' },
            { field: 'order.email',              save_as: 'customer_email', label: 'Customer Email' },
        ],
    },
    zoho_crm: {
        name:        'Zoho CRM Lead',
        icon:        '👥',
        description: 'Create and update leads in Zoho CRM',
        method:      'POST',
        url:         'https://www.zohoapis.com/crm/v3/Leads',
        headers:     [{ key: 'Authorization', value: 'Zoho-oauthtoken {{access_token}}' }, { key: 'Content-Type', value: 'application/json' }],
        params:      [
            { name: 'access_token', description: 'Zoho OAuth access token', required: true,  type: 'string' },
            { name: 'first_name',   description: 'Lead first name',         required: true,  type: 'string' },
            { name: 'last_name',    description: 'Lead last name',          required: false, type: 'string' },
            { name: 'phone',        description: 'Lead phone number',       required: true,  type: 'string' },
            { name: 'email',        description: 'Lead email address',      required: false, type: 'string' },
            { name: 'lead_source',  description: 'Lead source',             required: false, type: 'string' },
        ],
        body_template: '{"data": [{"First_Name": "{{first_name}}", "Last_Name": "{{last_name}}", "Phone": "{{phone}}", "Email": "{{email}}", "Lead_Source": "{{lead_source}}"}]}',
        response_mapping: [
            { field: 'data.0.details.id', save_as: 'lead_id',     label: 'Lead ID' },
            { field: 'data.0.status',     save_as: 'lead_status', label: 'Status' },
        ],
    },
};

const METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];
const CONNECTOR_TYPES = [
    { value: 'custom',          label: 'Custom API',      icon: '🔧' },
    { value: 'google_calendar', label: 'Google Calendar', icon: '📅' },
    { value: 'razorpay',        label: 'Razorpay',        icon: '💳' },
    { value: 'shopify',         label: 'Shopify',         icon: '🛍️' },
    { value: 'zoho_crm',        label: 'Zoho CRM',        icon: '👥' },
];

const Label  = ({ children, required }) => (
    <label style={{ fontSize: 12, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 6 }}>
        {children} {required && <span style={{ color: '#dc2626' }}>*</span>}
    </label>
);
const Hint   = ({ children }) => <p style={{ fontSize: 11, color: '#9ca3af', margin: '4px 0 0' }}>{children}</p>;
const Field  = ({ children, style }) => <div style={{ marginBottom: 18, ...style }}>{children}</div>;
const Input  = (props) => (
    <input {...props} style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #e5e7eb', borderRadius: 10, fontSize: 13, outline: 'none', boxSizing: 'border-box', ...props.style }}
        onFocus={e => e.target.style.borderColor = '#1565c0'}
        onBlur={e => e.target.style.borderColor = '#e5e7eb'}/>
);
const Section = ({ title, children }) => (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: 20, marginBottom: 20 }}>
        <p style={{ fontSize: 14, fontWeight: 700, color: '#111827', margin: '0 0 16px' }}>{title}</p>
        {children}
    </div>
);

export default function CreateTool() {
    const navigate       = useNavigate();
    const [searchParams] = useSearchParams();
    const toolId         = searchParams.get('id');
    const [loading, setLoading] = useState(!!toolId);
    const [saving,  setSaving]  = useState(false);

    const [form, setForm] = useState({
        name:             '',
        description:      '',
        icon:             '🔧',
        method:           'GET',
        url:              '',
        connector_type:    'custom',
        headers:           [],
        params:            [],
        body_template:     '',
        response_mapping:  [],
        cache_enabled:     false,
        cache_ttl_seconds: 0,
        is_stateful:       false,
        terminal_states:   [],
        check_interval_min: 60,
        track_field:       'status',
        is_active:         true,
    });

    const token = localStorage.getItem('token');
    const set   = (k, v) => setForm(p => ({ ...p, [k]: v }));

    useEffect(() => {
        if (toolId) {
            (async () => {
                try {
                    const res  = await fetch(`${url}/tools/${toolId}`, { headers: { Authorization: `Bearer ${token}` } });
                    const data = await res.json();
                    if (data.success) setForm(p => ({ ...p, ...data.data }));
                } catch (e) { toast.error('Failed to load tool'); }
                setLoading(false);
            })();
        }
    }, [toolId]);

    const applyTemplate = (type) => {
        const tpl = CONNECTOR_TEMPLATES[type];
        if (!tpl) return;
        setForm(p => ({ ...p, ...tpl, connector_type: type }));
        toast.success(`${tpl.name} template applied!`, { autoClose: 1500 });
    };

    const addHeader = () => set('headers', [...form.headers, { key: '', value: '' }]);
    const updateHeader = (i, field, val) => {
        const h = [...form.headers];
        h[i][field] = val;
        set('headers', h);
    };
    const removeHeader = (i) => set('headers', form.headers.filter((_, j) => j !== i));

    const addParam = () => set('params', [...form.params, { name: '', description: '', required: true, type: 'string' }]);
    const updateParam = (i, field, val) => {
        const p = [...form.params];
        p[i][field] = val;
        set('params', p);
    };
    const removeParam = (i) => set('params', form.params.filter((_, j) => j !== i));

    const addMapping = () => set('response_mapping', [...form.response_mapping, { field: '', save_as: '', label: '' }]);
    const updateMapping = (i, field, val) => {
        const m = [...form.response_mapping];
        m[i][field] = val;
        set('response_mapping', m);
    };
    const removeMapping = (i) => set('response_mapping', form.response_mapping.filter((_, j) => j !== i));

    const save = async () => {
        if (!form.name.trim())  { toast.error('Tool name is required'); return; }
        if (!form.url.trim())   { toast.error('URL is required'); return; }
        if (!form.description.trim()) { toast.error('Description is required'); return; }
        setSaving(true);
        try {
            const method   = toolId ? 'PATCH' : 'POST';
            const endpoint = toolId ? `${url}/tools/${toolId}` : `${url}/tools`;
            const res      = await fetch(endpoint, {
                method,
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body:    JSON.stringify(form),
            });
            const data = await res.json();
            if (data.success) {
                toast.success(toolId ? 'Tool updated ✅' : 'Tool created ✅', { autoClose: 2000 });
                setTimeout(() => navigate('/tools'), 1500);
            } else {
                toast.error(data.error);
            }
        } catch (e) { toast.error('Save failed'); }
        setSaving(false);
    };

    if (loading) return (
        <WrapperComponent>
            <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
                <div className="spinner-border text-primary"></div>
            </div>
        </WrapperComponent>
    );

    return (
        <WrapperComponent>
            <div style={{ maxWidth: 800, margin: '0 auto', fontFamily: "'Segoe UI', system-ui, sans-serif" }}>

                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 28 }}>
                    <button onClick={() => navigate('/tools')}
                        style={{ background: '#f3f4f6', border: 'none', borderRadius: 8, padding: '8px 12px', cursor: 'pointer', color: '#374151' }}>
                        <i className="bi bi-arrow-left"></i>
                    </button>
                    <div>
                        <h5 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#111827' }}>
                            {toolId ? 'Edit Tool' : 'Add Tool'}
                        </h5>
                        <p style={{ margin: '2px 0 0', fontSize: 13, color: '#6b7280' }}>
                            Define an API tool your agents can call during conversations
                        </p>
                    </div>
                </div>

                {/* Connector type / Template selector */}
                {!toolId && (
                    <Section title="Choose Connector Type">
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10 }}>
                            {CONNECTOR_TYPES.map(ct => (
                                <div key={ct.value}
                                    onClick={() => {
                                        set('connector_type', ct.value);
                                        if (ct.value !== 'custom') applyTemplate(ct.value);
                                    }}
                                    style={{ padding: '14px 12px', border: `2px solid ${form.connector_type === ct.value ? '#1565c0' : '#e5e7eb'}`, borderRadius: 12, cursor: 'pointer', background: form.connector_type === ct.value ? '#eff6ff' : '#fff', textAlign: 'center', transition: 'all 0.15s' }}
                                    onMouseEnter={e => { if (form.connector_type !== ct.value) e.currentTarget.style.borderColor = '#bfdbfe'; }}
                                    onMouseLeave={e => { if (form.connector_type !== ct.value) e.currentTarget.style.borderColor = '#e5e7eb'; }}>
                                    <div style={{ fontSize: 24, marginBottom: 6 }}>{ct.icon}</div>
                                    <p style={{ fontSize: 11, fontWeight: 700, color: form.connector_type === ct.value ? '#1565c0' : '#374151', margin: 0 }}>{ct.label}</p>
                                </div>
                            ))}
                        </div>
                        {form.connector_type !== 'custom' && (
                            <div style={{ marginTop: 14, padding: '10px 14px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, display: 'flex', gap: 8 }}>
                                <i className="bi bi-check-circle-fill" style={{ color: '#16a34a', fontSize: 14, marginTop: 1 }}></i>
                                <p style={{ margin: 0, fontSize: 12, color: '#16a34a' }}>
                                    Template applied! Review and customize the pre-filled settings below.
                                </p>
                            </div>
                        )}
                    </Section>
                )}

                {/* Basic Info */}
                <Section title="Basic Information">
                    <div style={{ display: 'grid', gridTemplateColumns: '60px 1fr', gap: 12, marginBottom: 18 }}>
                        <Field style={{ marginBottom: 0 }}>
                            <Label>Icon</Label>
                            <Input value={form.icon} onChange={e => set('icon', e.target.value)}
                                style={{ textAlign: 'center', fontSize: 22, padding: '6px' }}/>
                        </Field>
                        <Field style={{ marginBottom: 0 }}>
                            <Label required>Tool Name</Label>
                            <Input value={form.name} onChange={e => set('name', e.target.value)}
                                placeholder="e.g. Check Order Status, Book Appointment"/>
                        </Field>
                    </div>
                    <Field>
                        <Label required>Description</Label>
                        <Input value={form.description} onChange={e => set('description', e.target.value)}
                            placeholder="Describe what this tool does — agent uses this to decide when to call it"/>
                        <Hint>Be specific — agent reads this description to decide when to use this tool</Hint>
                    </Field>
                </Section>

                {/* API Configuration */}
                <Section title="API Configuration">
                    <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 12, marginBottom: 18 }}>
                        <Field style={{ marginBottom: 0 }}>
                            <Label required>Method</Label>
                            <select value={form.method} onChange={e => set('method', e.target.value)}
                                style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #e5e7eb', borderRadius: 10, fontSize: 13, outline: 'none' }}>
                                {METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                            </select>
                        </Field>
                        <Field style={{ marginBottom: 0 }}>
                            <Label required>URL</Label>
                            <Input value={form.url} onChange={e => set('url', e.target.value)}
                                placeholder="https://api.example.com/orders/{{order_id}}"/>
                        </Field>
                    </div>
                    <Hint>Use {'{{variable_name}}'} for dynamic values that will be filled at runtime</Hint>
                </Section>

                {/* Headers */}
                <Section title="Headers">
                    {form.headers.map((h, i) => (
                        <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 8, marginBottom: 8 }}>
                            <Input value={h.key} onChange={e => updateHeader(i, 'key', e.target.value)} placeholder="Header name (e.g. Authorization)"/>
                            <Input value={h.value} onChange={e => updateHeader(i, 'value', e.target.value)} placeholder="Value (e.g. Bearer {{token}})"/>
                            <button onClick={() => removeHeader(i)}
                                style={{ padding: '9px 12px', background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 10, cursor: 'pointer', fontSize: 14 }}>
                                <i className="bi bi-x"></i>
                            </button>
                        </div>
                    ))}
                    <button onClick={addHeader}
                        style={{ padding: '8px 16px', background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 10, cursor: 'pointer', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <i className="bi bi-plus"></i> Add Header
                    </button>
                </Section>

                {/* Input Parameters */}
                <Section title="Input Parameters">
                    <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 14px' }}>
                        Parameters the agent will collect from the conversation before calling this tool.
                    </p>
                    {form.params.map((p, i) => (
                        <div key={i} style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 10, padding: '14px', marginBottom: 10 }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 8, marginBottom: 8 }}>
                                <Input value={p.name} onChange={e => updateParam(i, 'name', e.target.value)} placeholder="param_name"/>
                                <Input value={p.description} onChange={e => updateParam(i, 'description', e.target.value)} placeholder="What is this parameter?"/>
                                <button onClick={() => removeParam(i)}
                                    style={{ padding: '9px 12px', background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 10, cursor: 'pointer' }}>
                                    <i className="bi bi-x"></i>
                                </button>
                            </div>
                            <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                                <select value={p.type} onChange={e => updateParam(i, 'type', e.target.value)}
                                    style={{ padding: '6px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 12, outline: 'none' }}>
                                    <option value="string">String</option>
                                    <option value="number">Number</option>
                                    <option value="boolean">Boolean</option>
                                </select>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#374151', cursor: 'pointer' }}>
                                    <input type="checkbox" checked={p.required} onChange={e => updateParam(i, 'required', e.target.checked)}/>
                                    Required
                                </label>
                            </div>
                        </div>
                    ))}
                    <button onClick={addParam}
                        style={{ padding: '8px 16px', background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 10, cursor: 'pointer', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <i className="bi bi-plus"></i> Add Parameter
                    </button>
                </Section>

                {/* Body Template */}
                {['POST', 'PUT', 'PATCH'].includes(form.method) && (
                    <Section title="Request Body">
                        <Label>Body Template (JSON)</Label>
                        <textarea value={form.body_template} onChange={e => set('body_template', e.target.value)}
                            rows={6} placeholder={'{\n  "order_id": "{{order_id}}",\n  "customer": "{{customer_name}}"\n}'}
                            style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: 10, fontSize: 12, outline: 'none', fontFamily: 'monospace', resize: 'vertical', boxSizing: 'border-box' }}
                            onFocus={e => e.target.style.borderColor = '#1565c0'}
                            onBlur={e => e.target.style.borderColor = '#e5e7eb'}/>
                        <Hint>Use {'{{variable_name}}'} for dynamic values from conversation</Hint>
                    </Section>
                )}

                {/* Response Mapping */}
                <Section title="Response Mapping">
                    <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 14px' }}>
                        Extract specific fields from API response and save them for agent to use.
                    </p>
                    {form.response_mapping.map((m, i) => (
                        <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 8, marginBottom: 8 }}>
                            <Input value={m.field} onChange={e => updateMapping(i, 'field', e.target.value)} placeholder="API field (e.g. data.status)"/>
                            <Input value={m.save_as} onChange={e => updateMapping(i, 'save_as', e.target.value)} placeholder="Save as (e.g. order_status)"/>
                            <Input value={m.label} onChange={e => updateMapping(i, 'label', e.target.value)} placeholder="Label (e.g. Order Status)"/>
                            <button onClick={() => removeMapping(i)}
                                style={{ padding: '9px 12px', background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 10, cursor: 'pointer' }}>
                                <i className="bi bi-x"></i>
                            </button>
                        </div>
                    ))}
                    <button onClick={addMapping}
                        style={{ padding: '8px 16px', background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 10, cursor: 'pointer', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <i className="bi bi-plus"></i> Add Mapping
                    </button>
                    <Hint>Use dot notation for nested fields: data.order.status</Hint>
                </Section>

                {/* Stateful Tracking */}
                <Section title="Stateful Tracking (Agent Memory)">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f3f4f6', marginBottom: 16 }}>
                        <div>
                            <p style={{ fontSize: 13, fontWeight: 600, color: '#374151', margin: 0 }}>Track Status Changes</p>
                            <p style={{ fontSize: 11, color: '#9ca3af', margin: '2px 0 0' }}>
                                Enable for tools with changing states (order status, loan status, ticket status).
                                Agent Memory will auto-check and notify customer when status changes.
                            </p>
                        </div>
                        <div onClick={() => set('is_stateful', !form.is_stateful)}
                            style={{ width: 44, height: 24, borderRadius: 12, background: form.is_stateful ? '#7c3aed' : '#d1d5db', cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
                            <div style={{ position: 'absolute', top: 2, left: form.is_stateful ? 22 : 2, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }}/>
                        </div>
                    </div>

                    {form.is_stateful && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                            <div>
                                <Label tooltip="Which field in API response contains the current state/status">
                                    Track Field
                                </Label>
                                <Input value={form.track_field} onChange={e => set('track_field', e.target.value)}
                                    placeholder="e.g. status, state, current_status"/>
                                <Hint>The response field that changes over time (e.g. "status", "order_status")</Hint>
                            </div>
                            <div>
                                <Label tooltip="States that mean the process is complete — stop checking after these">
                                    Terminal States (comma separated)
                                </Label>
                                <Input
                                    value={form.terminal_states.join(', ')}
                                    onChange={e => set('terminal_states', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                                    placeholder="e.g. delivered, cancelled, completed, resolved"/>
                                <Hint>When status reaches any of these values — stop auto-checking</Hint>
                            </div>
                            <div>
                                <Label tooltip="How often to re-check the status when customer returns">
                                    Check Interval (minutes)
                                </Label>
                                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                                    {[
                                        { label: '5 min',   value: 5,    desc: 'Food delivery' },
                                        { label: '30 min',  value: 30,   desc: 'Courier' },
                                        { label: '1 hour',  value: 60,   desc: 'General' },
                                        { label: '4 hours', value: 240,  desc: 'Loan/Ticket' },
                                        { label: '1 day',   value: 1440, desc: 'Insurance' },
                                    ].map(opt => (
                                        <div key={opt.value} onClick={() => set('check_interval_min', opt.value)}
                                            style={{ padding: '10px 14px', border: `2px solid ${form.check_interval_min === opt.value ? '#7c3aed' : '#e5e7eb'}`, borderRadius: 10, cursor: 'pointer', background: form.check_interval_min === opt.value ? '#faf5ff' : '#fff', textAlign: 'center', minWidth: 80 }}>
                                            <p style={{ fontSize: 12, fontWeight: 700, color: form.check_interval_min === opt.value ? '#7c3aed' : '#374151', margin: '0 0 2px' }}>{opt.label}</p>
                                            <p style={{ fontSize: 10, color: '#9ca3af', margin: 0 }}>{opt.desc}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </Section>

                {/* Cache Settings */}
                <Section title="Cache Settings">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f3f4f6', marginBottom: 16 }}>
                        <div>
                            <p style={{ fontSize: 13, fontWeight: 600, color: '#374151', margin: 0 }}>Enable Response Caching</p>
                            <p style={{ fontSize: 11, color: '#9ca3af', margin: '2px 0 0' }}>
                                Cache API responses to improve speed. Disable for real-time data (order status, payments, live tracking).
                            </p>
                        </div>
                        <div onClick={() => set('cache_enabled', !form.cache_enabled)}
                            style={{ width: 44, height: 24, borderRadius: 12, background: form.cache_enabled ? '#1565c0' : '#d1d5db', cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
                            <div style={{ position: 'absolute', top: 2, left: form.cache_enabled ? 22 : 2, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }}/>
                        </div>
                    </div>

                    {form.cache_enabled && (
                        <div>
                            <Label tooltip="How long to keep cached responses. Choose based on how frequently your data changes.">
                                Cache Duration
                            </Label>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10 }}>
                                {[
                                    { label: '5 minutes',  value: 300,   desc: 'Fast changing data',   icon: '⚡', color: '#dc2626', bg: '#fef2f2' },
                                    { label: '30 minutes', value: 1800,  desc: 'Moderate changes',     icon: '🔄', color: '#d97706', bg: '#fffbeb' },
                                    { label: '1 hour',     value: 3600,  desc: 'Slow changing data',   icon: '🕐', color: '#16a34a', bg: '#f0fdf4' },
                                    { label: '6 hours',    value: 21600, desc: 'Rarely changes',       icon: '📅', color: '#1565c0', bg: '#eff6ff' },
                                    { label: '24 hours',   value: 86400, desc: 'Daily updates only',   icon: '🗓️', color: '#7c3aed', bg: '#faf5ff' },
                                ].map(opt => (
                                    <div key={opt.value} onClick={() => set('cache_ttl_seconds', opt.value)}
                                        style={{ padding: '12px', border: `2px solid ${form.cache_ttl_seconds === opt.value ? opt.color : '#e5e7eb'}`, borderRadius: 10, cursor: 'pointer', background: form.cache_ttl_seconds === opt.value ? opt.bg : '#fff', textAlign: 'center', transition: 'all 0.15s' }}
                                        onMouseEnter={e => { if (form.cache_ttl_seconds !== opt.value) e.currentTarget.style.borderColor = opt.color; }}
                                        onMouseLeave={e => { if (form.cache_ttl_seconds !== opt.value) e.currentTarget.style.borderColor = '#e5e7eb'; }}>
                                        <div style={{ fontSize: 20, marginBottom: 4 }}>{opt.icon}</div>
                                        <p style={{ fontSize: 12, fontWeight: 700, color: form.cache_ttl_seconds === opt.value ? opt.color : '#374151', margin: '0 0 2px' }}>{opt.label}</p>
                                        <p style={{ fontSize: 10, color: '#9ca3af', margin: 0 }}>{opt.desc}</p>
                                    </div>
                                ))}
                            </div>
                            <div style={{ marginTop: 12, padding: '10px 14px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, display: 'flex', gap: 8 }}>
                                <span style={{ fontSize: 14 }}>⚠️</span>
                                <p style={{ margin: 0, fontSize: 11, color: '#92400e' }}>
                                    <strong>Never cache:</strong> Order status, payment status, OTP, live tracking, account balance, seat availability or any real-time data.
                                    Caching such data will show outdated information to users.
                                </p>
                            </div>
                        </div>
                    )}

                    {!form.cache_enabled && (
                        <div style={{ padding: '10px 14px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, display: 'flex', gap: 8 }}>
                            <i className="bi bi-check-circle-fill" style={{ color: '#16a34a', fontSize: 14, flexShrink: 0, marginTop: 1 }}></i>
                            <p style={{ margin: 0, fontSize: 11, color: '#16a34a' }}>
                                Caching is disabled — every API call will fetch fresh real-time data. Recommended for order status, payments, and live data.
                            </p>
                        </div>
                    )}
                </Section>

                {/* Save button */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, paddingTop: 8 }}>
                    <button onClick={() => navigate('/tools')}
                        style={{ padding: '10px 20px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                        Cancel
                    </button>
                    <button onClick={save} disabled={saving}
                        style={{ padding: '10px 28px', background: saving ? '#d1d5db' : 'linear-gradient(135deg, #1565c0, #1e40af)', color: '#fff', border: 'none', borderRadius: 10, cursor: saving ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
                        {saving ? <><div className="spinner-border spinner-border-sm text-light" style={{ width: '1rem', height: '1rem' }}></div> Saving...</>
                            : <><i className="bi bi-check-lg"></i> {toolId ? 'Save Changes' : 'Create Tool'}</>}
                    </button>
                </div>

            </div>
            <ToastContainer/>
        </WrapperComponent>
    );
}
